import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';

import {
    ccApiUrl,
    getConnectedCenterUploadUrl,
    isConnectedCenterMode
} from '../../lib/nexus-connected-center';
import {
    ccParentApiFetch,
    ccParentApiPostForm,
    shouldUseParentBridge
} from '../../lib/nexus-cc-parent-bridge';

import menuBarStyles from './menu-bar.css';
import styles from './connected-center-projects.css';

class ConnectedCenterProjects extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            open: false,
            loading: false,
            games: [],
            savingId: null
        };
        bindAll(this, [
            'openModal',
            'closeModal',
            'loadGames',
            'editGame',
            'deleteGame',
            'changeVisibility'
        ]);
    }

    openModal () {
        this.setState({open: true});
        this.loadGames();
    }

    closeModal () {
        this.setState({open: false});
    }

    loadGames () {
        this.setState({loading: true});
        const load = shouldUseParentBridge() ?
            ccParentApiFetch('get_my_community_games.php') :
            fetch(ccApiUrl('get_my_community_games.php'), {credentials: 'include'}).then(r => r.json());

        load
            .then(data => {
                this.setState({games: data.games || [], loading: false});
            })
            .catch(() => {
                this.setState({loading: false, games: []});
            });
    }

    editGame (game) {
        const downloadUrl = ccApiUrl(`download_community_game.php?id=${game.id}`);
        const uploadBase = getConnectedCenterUploadUrl();
        const params = new URLSearchParams(window.location.search);
        params.set('connected_center', '1');
        params.set('cc_upload', uploadBase);
        params.set('cc_game_id', String(game.id));
        params.set('project_url', downloadUrl);
        if (game.title) {
            params.set('cc_game_title', game.title);
        }
        params.delete('project');
        params.delete('id');
        window.location.search = params.toString();
    }

    deleteGame (game) {
        if (!window.confirm(`Delete "${game.title}"? This cannot be undone.`)) {
            return;
        }
        this.setState({savingId: game.id});
        const runDelete = shouldUseParentBridge() ?
            ccParentApiFetch('delete_community_game.php', {
                method: 'POST',
                jsonBody: {game_id: game.id}
            }) :
            fetch(ccApiUrl('delete_community_game.php'), {
                method: 'POST',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({game_id: game.id})
            }).then(r => r.json());

        runDelete
            .then(data => {
                if (data.error) throw new Error(data.error);
                this.loadGames();
                try {
                    window.parent.postMessage({type: 'cc-game-deleted', gameId: game.id}, '*');
                } catch (e) {
                    // ignore
                }
            })
            .catch(() => {})
            .finally(() => this.setState({savingId: null}));
    }

    changeVisibility (game, visibility) {
        this.setState({savingId: game.id});
        const runUpdate = shouldUseParentBridge() ?
            ccParentApiPostForm('update_community_game.php', {
                game_id: String(game.id),
                title: game.title,
                visibility
            }) :
            (() => {
                const form = new FormData();
                form.append('game_id', String(game.id));
                form.append('title', game.title);
                form.append('visibility', visibility);
                return fetch(ccApiUrl('update_community_game.php'), {
                    method: 'POST',
                    body: form,
                    credentials: 'include'
                }).then(r => r.json());
            })();

        runUpdate
            .then(data => {
                if (data.error) throw new Error(data.error);
                this.setState(state => ({
                    games: state.games.map(g => (g.id === game.id ? data.game : g))
                }));
            })
            .catch(() => {})
            .finally(() => this.setState({savingId: null}));
    }

    renderModal () {
        if (!this.state.open) {
            return null;
        }

        const {games, loading, savingId} = this.state;

        return ReactDOM.createPortal(
            <div
                className={styles.overlay}
                onClick={this.closeModal}
            >
                <div
                    className={styles.modal}
                    onClick={e => e.stopPropagation()}
                >
                    <div className={styles.header}>
                        <span className={styles.title}>
                            <FormattedMessage
                                defaultMessage="My Community Projects"
                                description="Title for modal listing user's published community games"
                                id="tw.menuBar.myCommunityProjects"
                            />
                        </span>
                        <button
                            className={styles.closeBtn}
                            type="button"
                            onClick={this.closeModal}
                        >
                            ✕
                        </button>
                    </div>
                    <div className={styles.list}>
                        {loading ? (
                            <div className={styles.loading}>
                                <FormattedMessage
                                    defaultMessage="Loading…"
                                    id="tw.menuBar.loadingProjects"
                                />
                            </div>
                        ) : games.length === 0 ? (
                            <div className={styles.empty}>
                                <FormattedMessage
                                    defaultMessage="No published projects yet. Use Publish to Community to share your first game!"
                                    id="tw.menuBar.noProjects"
                                />
                            </div>
                        ) : games.map(game => (
                            <div
                                key={game.id}
                                className={styles.row}
                            >
                                <div
                                    className={styles.emoji}
                                    style={{background: game.gradient}}
                                >
                                    {game.emoji}
                                </div>
                                <div className={styles.meta}>
                                    <div className={styles.gameTitle}>{game.title}</div>
                                    <div className={styles.sub}>
                                        {`ID ${game.id} · ${game.play_count} plays`}
                                    </div>
                                </div>
                                <select
                                    className={styles.visibilitySelect}
                                    disabled={savingId === game.id}
                                    value={game.visibility || 'public'}
                                    onChange={e => this.changeVisibility(game, e.target.value)}
                                >
                                    <option value="public">Public</option>
                                    <option value="unlisted">Unlisted</option>
                                    <option value="private">Private</option>
                                </select>
                                <div className={styles.actions}>
                                    <button
                                        className={`${styles.actionBtn} ${styles.editBtn}`}
                                        type="button"
                                        onClick={() => this.editGame(game)}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                        disabled={savingId === game.id}
                                        type="button"
                                        onClick={() => this.deleteGame(game)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    render () {
        if (!isConnectedCenterMode()) {
            return null;
        }

        return (
            <React.Fragment>
                <div
                    className={classNames(
                        menuBarStyles.menuBarItem,
                        menuBarStyles.menuBarGlassBtn,
                        menuBarStyles.hoverable
                    )}
                    role="button"
                    tabIndex={0}
                    title="Manage your published community games"
                    onClick={this.openModal}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.openModal();
                        }
                    }}
                >
                    <span className={styles.glassBtnLabel}>
                        <FormattedMessage
                            defaultMessage="My Projects"
                            description="Menu bar button to open published community games manager"
                            id="tw.menuBar.myProjectsBtn"
                        />
                    </span>
                </div>
                {this.renderModal()}
            </React.Fragment>
        );
    }
}

ConnectedCenterProjects.propTypes = {
    className: PropTypes.string
};

export default ConnectedCenterProjects;
